import React, { createContext, useContext, useMemo, useState } from 'react';
import {
  IDefaultViewPreference,
  useDefaultViewPreference,
} from '../hooks/useDefaultViewPreference';

type FavoriteListKey = keyof IDefaultViewPreference;
type ActiveFavoriteState = Partial<Record<FavoriteListKey, boolean>>;

interface IListViewPreferenceContext {
  preference: IDefaultViewPreference;
  updatePreference: (updates: Partial<IDefaultViewPreference>) => void;
  getActiveFavorite: (key: FavoriteListKey) => boolean;
  setActiveFavorite: (key: FavoriteListKey, value: boolean) => void;
}

const ListViewPreferenceContext = createContext<IListViewPreferenceContext | undefined>(undefined);

export const ListViewPreferenceProvider = ({ children }: { children: React.ReactNode }) => {
  const { preference, updatePreference } = useDefaultViewPreference();
  const [favoriteOverrides, setFavoriteOverrides] = useState<ActiveFavoriteState>({});

  const updatePreferenceAndActiveFavorite = (updates: Partial<IDefaultViewPreference>) => {
    updatePreference(updates);
    setFavoriteOverrides((prev) => ({ ...prev, ...updates }));
  };

  const setActiveFavorite = (key: FavoriteListKey, value: boolean) => {
    setFavoriteOverrides((prev) => ({ ...prev, [key]: value }));
  };

  const getActiveFavorite = (key: FavoriteListKey) => favoriteOverrides[key] ?? preference[key];

  const value = useMemo(
    () => ({
      preference,
      updatePreference: updatePreferenceAndActiveFavorite,
      getActiveFavorite,
      setActiveFavorite,
    }),
    [preference, favoriteOverrides],
  );

  return (
    <ListViewPreferenceContext.Provider value={value}>
      {children}
    </ListViewPreferenceContext.Provider>
  );
};

export const useListViewPreference = (): IListViewPreferenceContext => {
  const context = useContext(ListViewPreferenceContext);
  if (!context) {
    throw new Error('useListViewPreference must be used within a ListViewPreferenceProvider');
  }
  return context;
};
